/**
 * relatorioDeGarantia.js — "Quem fez o quê" e "O que é determinístico" do relatório de
 * garantia da entrega (SPEC-AGENTES-A5 FR-16/FR-17).
 *
 * A tela NÃO calcula nada: lê as duas seções do relatório que o worker gravou na entrega
 * (`reports/software-assurance-report.md`), servido por
 * `GET /executions/{id}/assurance-report.md` (executions:view). O `.json` irmão
 * (`agentes[]`, `deterministicos[]`) ainda não tem rota na API; quando tiver,
 * `relatorioDoJson` já converte o formato do §6 para o mesmo quadro.
 */
import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiContext } from './ApiContext';

export const TITULO_QUEM_FEZ = 'Quem fez o quê';
export const TITULO_DETERMINISTICO = 'O que é determinístico (não é agente)';

const COLUNAS_JSON = ['Etapa', 'Agente', 'Prompt', 'Tier', 'Modelo', 'Custo (US$)',
  'Delegações', 'Achados (alta/média/baixa)'];

function celulas(linha) {
  const s = linha.trim().replace(/^\|/, '').replace(/\|$/, '');
  return s.split('|').map((c) => c.trim());
}

function fimDeSecao(linha) {
  const t = linha.trim();
  return t.startsWith('#') || t === '---' || t.startsWith('<!--');
}

/**
 * Lê as duas seções do `.md` do relatório. Multi-repo: a API junta um relatório por
 * repositório; o quadro é da EXECUÇÃO (igual em todos), então vale a primeira ocorrência.
 * @param {string} md
 * @returns {{colunas: string[], linhas: string[][], deterministico: string,
 *            deterministicos: string[]} | null} null quando nenhuma das seções existe
 */
export function relatorioDoMarkdown(md) {
  if (!md || typeof md !== 'string') return null;
  const linhas = md.split(/\r?\n/);
  let colunas = [];
  const corpo = [];
  const iQuem = linhas.findIndex((l) => l.trim() === `## ${TITULO_QUEM_FEZ}`);
  if (iQuem >= 0) {
    for (let i = iQuem + 1; i < linhas.length; i += 1) {
      const l = linhas[i];
      if (fimDeSecao(l)) break;
      if (!l.trim().startsWith('|')) continue;
      if (/^\|\s*:?-{3,}/.test(l.trim())) continue;
      if (colunas.length === 0) colunas = celulas(l);
      else corpo.push(celulas(l));
    }
  }
  const texto = [];
  const iDet = linhas.findIndex((l) => l.trim() === `## ${TITULO_DETERMINISTICO}`);
  if (iDet >= 0) {
    for (let i = iDet + 1; i < linhas.length; i += 1) {
      if (fimDeSecao(linhas[i])) break;
      if (linhas[i].trim()) texto.push(linhas[i].trim());
    }
  }
  if (iQuem < 0 && iDet < 0) return null;
  return { colunas, linhas: corpo, deterministico: texto.join(' '), deterministicos: [] };
}

/**
 * O JSON do relatório (§6 `AssuranceAgentes`) no mesmo quadro — só formatação de
 * exibição, sem cálculo.
 * @param {{agentes?: object[], deterministicos?: string[]}} json
 */
export function relatorioDoJson(json) {
  if (!json || typeof json !== 'object') return null;
  const agentes = Array.isArray(json.agentes) ? json.agentes : [];
  const det = Array.isArray(json.deterministicos) ? json.deterministicos.map(String) : [];
  if (agentes.length === 0 && det.length === 0) return null;
  const linhas = agentes.map((a) => {
    const ach = a.achados && typeof a.achados === 'object' ? a.achados : null;
    return [
      String(a.etapa ?? ''), String(a.agente ?? ''),
      a.prompt_version ? `v${a.prompt_version}` : 'catálogo',
      a.tier || '—', a.resolved_model || '—',
      typeof a.custo_usd === 'number'
        ? a.custo_usd.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '—',
      String(a.delegacoes_feitas ?? 0),
      ach ? `${ach.alta ?? 0}/${ach.media ?? 0}/${ach.baixa ?? 0}` : '—',
    ];
  });
  return { colunas: agentes.length ? COLUNAS_JSON : [], linhas, deterministico: '',
    deterministicos: det };
}

/**
 * Hook: o relatório de garantia da execução, já reduzido às duas seções.
 * Tolerante: sem `ApiProvider` (testes de tela) ou sem execução, fica desligado.
 * 404 (entrega sem relatório) → `data: null`, sem erro na tela.
 * @param {string|null|undefined} executionId
 */
export function useRelatorioDeGarantia(executionId) {
  const ctx = useContext(ApiContext);
  const api = ctx?.client ?? null;
  return useQuery({
    queryKey: ['executions', executionId, 'assurance-report', 'quem-fez'],
    enabled: Boolean(api && executionId),
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const md = await api.getText(`/executions/${executionId}/assurance-report.md`);
        return relatorioDoMarkdown(md);
      } catch (e) {
        if (e?.status === 404) return null;
        throw e;
      }
    },
  });
}
